import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import Footer from '@/components/Footer';
import { UserAvatar } from '@/components/UserAvatar';
import NewTripDialog from '@/components/NewTripDialog';
import PinIdeaDialog from '@/components/PinIdeaDialog';
import StartPlanningDialog from '@/components/StartPlanningDialog';
import SaveIdeaDialog from '@/components/SaveIdeaDialog';
import IdeaCard from '@/components/IdeaCard';
import IdeaDetailDialog from '@/components/IdeaDetailDialog';
import OnboardingModal from '@/components/OnboardingModal';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { HOME } from '@/constants/testIds';
import { Plus, Bookmark, Calendar, Users, ChevronRight, Pin, Sparkles } from 'lucide-react';
import { format, parseISO } from 'date-fns';

function formatDateRange(start, end) {
    try {
        const s = parseISO(start);
        const e = parseISO(end);
        if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
            return `${format(s, 'MMM d')} – ${format(e, 'd, yyyy')}`;
        }
        if (s.getFullYear() === e.getFullYear()) {
            return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
        }
        return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
    } catch { return `${start} – ${end}`; }
}

const SectionHeader = ({ eyebrow, title, action }) => (
    <div className="flex items-end justify-between mb-5 sm:mb-7">
        <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">{eyebrow}</div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--ns-text)]">{title}</h2>
        </div>
        {action}
    </div>
);

const TripCard = ({ trip }) => {
    const participants = trip.participants || [];
    const shown = participants.slice(0, 4);
    const extra = Math.max(0, participants.length - 4);
    const prefsPct = trip.preferences_total ? Math.round((trip.preferences_submitted / trip.preferences_total) * 100) : 0;

    return (
        <Link to={`/trips/${trip.trip_id}`} data-testid={HOME.tripCard} className="block group">
            <div className="ns-card ns-card-hover p-6 sm:p-7 h-full relative">
                {trip.is_premium && (
                    <span
                        data-testid="trip-card-premium-badge"
                        className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest"
                        style={{ background: 'var(--ns-clay)', color: 'white' }}
                    >
                        <Sparkles size={10} /> Premium
                    </span>
                )}
                <div className="flex items-start justify-between">
                    <div className={trip.is_premium ? 'pr-24' : ''}>
                        <div className="text-xs uppercase tracking-widest text-[var(--ns-clay)] font-medium">{trip.destination}</div>
                        <h3 className="font-display text-xl sm:text-2xl font-semibold mt-1 tracking-tight text-[var(--ns-text)]">{trip.name}</h3>
                    </div>
                    {!trip.is_premium && <ChevronRight className="text-[var(--ns-text3)] group-hover:text-[var(--ns-text)] transition" />}
                </div>
                <div className="flex items-center gap-2 mt-4 text-[var(--ns-text2)] text-sm">
                    <Calendar size={14} /> {formatDateRange(trip.start_date, trip.end_date)}
                </div>

                {/* Counters */}
                <div className="flex items-center gap-4 mt-5 text-xs text-[var(--ns-text2)]">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="font-semibold text-[var(--ns-text)]">{trip.ideas_count ?? 0}</span> ideas
                    </span>
                    <span className="w-px h-3 bg-[var(--ns-border)]" />
                    <span className="inline-flex items-center gap-1.5">
                        <span className="font-semibold text-[var(--ns-text)]">{trip.preferences_submitted ?? 0}/{trip.preferences_total ?? 0}</span> preferences
                    </span>
                    <span className="w-px h-3 bg-[var(--ns-border)]" />
                    <span className="inline-flex items-center gap-1.5">
                        <span className="font-semibold text-[var(--ns-text)]">{trip.votes_cast ?? 0}/{trip.votes_total ?? 0}</span> votes
                    </span>
                </div>

                <div className="flex items-center justify-between mt-6">
                    <div className="flex -space-x-2">
                        {shown.map((p) => (
                            <UserAvatar key={p.user_id} name={p.name} picture={p.picture} size={32} ring />
                        ))}
                        {extra > 0 && (
                            <div className="w-8 h-8 rounded-full ring-2 ring-white bg-[var(--ns-soft)] text-[var(--ns-text2)] text-xs font-medium flex items-center justify-center">
                                +{extra}
                            </div>
                        )}
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)]">Group ready</div>
                        <div className="w-32 h-1.5 rounded-full bg-[var(--ns-soft)] mt-1.5 overflow-hidden">
                            <div className="h-full transition-all" style={{ width: `${prefsPct}%`, background: 'var(--ns-clay)' }} />
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const WishlistRow = ({ item, onStart }) => (
    <div data-testid={HOME.wishlistItem} className="flex items-center justify-between py-4 px-4 rounded-xl hover:bg-[var(--ns-soft)] transition border-b border-[var(--ns-border)] last:border-0">
        <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--ns-soft)' }}>
                <Sparkles size={16} className="text-[var(--ns-clay)]" />
            </span>
            <span className="font-display text-base font-medium text-[var(--ns-text)]">{item.destination}</span>
        </div>
        <Button
            variant="outline"
            onClick={() => onStart(item)}
            data-testid={HOME.startPlanningButton}
            className="rounded-full border-[var(--ns-border)] text-[var(--ns-navy)] hover:bg-[var(--ns-navy)] hover:text-white hover:border-[var(--ns-navy)] transition h-9 px-4 text-sm"
        >
            Start planning
        </Button>
    </div>
);

export default function Dashboard() {
    const { user, refresh } = useAuth();
    const [trips, setTrips] = useState([]);
    const [inspirations, setInspirations] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [newTripOpen, setNewTripOpen] = useState(false);
    const [saveIdeaOpen, setSaveIdeaOpen] = useState(false);
    const [pinTarget, setPinTarget] = useState(null);
    const [startTarget, setStartTarget] = useState(null);
    const [detailIdea, setDetailIdea] = useState(null);
    const [onboardingOpen, setOnboardingOpen] = useState(false);

    // First-visit onboarding modal for direct signups. Show when user's onboarding_seen is
    // explicitly false (never for older users where the field is missing). Mark seen immediately
    // when the modal is shown so a page refresh never re-triggers it.
    useEffect(() => {
        if (user && user.onboarding_seen === false && !onboardingOpen) {
            setOnboardingOpen(true);
            api.patch('/auth/me/onboarding').then(() => {
                refresh?.();
            }).catch(() => { /* non-fatal */ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.user_id, user?.onboarding_seen]);

    const loadAll = async () => {
        try {
            const [t, i, w] = await Promise.all([
                api.get('/trips'),
                api.get('/inspirations'),
                api.get('/wishlist'),
            ]);
            setTrips(t.data.trips || []);
            setInspirations(i.data.inspirations || []);
            setWishlist(w.data.wishlist || []);
        } catch (e) {
            // Silent — protected route handles auth issues
        }
    };

    useEffect(() => { loadAll(); }, []);

    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return 'Good morning';
        if (h < 18) return 'Good afternoon';
        return 'Good evening';
    }, []);

    return (
        <div className="min-h-screen bg-[var(--ns-bg)] flex flex-col">
            <Header />
            <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {/* Greeting + CTAs */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">{greeting}</div>
                        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[var(--ns-text)]">
                            Hey {user?.name?.split(' ')[0] || 'there'}, where to next?
                        </h1>
                        <p className="text-[var(--ns-text2)] mt-3 text-lg max-w-xl leading-relaxed">
                            Your trips, ideas, and wishlist — all in one calm place.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            onClick={() => setNewTripOpen(true)}
                            data-testid={HOME.newTripButton}
                            className="rounded-full h-12 px-6 text-white font-medium hover:scale-[1.02] transition"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            <Plus size={18} className="mr-2" /> New trip
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setSaveIdeaOpen(true)}
                            data-testid={HOME.saveIdeaButton}
                            className="rounded-full h-12 px-6 border-[var(--ns-border)] text-[var(--ns-navy)] hover:bg-[var(--ns-soft)] transition"
                        >
                            <Bookmark size={16} className="mr-2" /> Save an idea
                        </Button>
                    </div>
                </div>

                {/* Active Trips */}
                <section className="mb-16" data-testid={HOME.activeTripsSection}>
                    <SectionHeader eyebrow="In motion" title="Active trips" />
                    {trips.length === 0 ? (
                        <div className="ns-card p-10 text-center">
                            <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--ns-soft)' }}>
                                <Calendar className="text-[var(--ns-clay)]" />
                            </div>
                            <h3 className="font-display text-xl font-semibold tracking-tight">No trips yet</h3>
                            <p className="text-[var(--ns-text2)] mt-2 max-w-md mx-auto">Start your first trip — invite friends, share inspiration, and decide together.</p>
                            <Button
                                onClick={() => setNewTripOpen(true)}
                                className="mt-6 rounded-full h-11 px-6 text-white"
                                style={{ background: 'var(--ns-navy)' }}
                            >
                                <Plus size={16} className="mr-2" /> Create your first trip
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {trips.map((t) => <TripCard key={t.trip_id} trip={t} />)}
                        </div>
                    )}
                </section>

                {/* Inspiration Board */}
                <section className="mb-16" data-testid={HOME.inspirationSection}>
                    <SectionHeader
                        eyebrow="For someday"
                        title="Inspiration board"
                        action={
                            <Button
                                variant="ghost"
                                onClick={() => setSaveIdeaOpen(true)}
                                className="rounded-full text-[var(--ns-navy)] hover:bg-[var(--ns-soft)] h-10"
                            >
                                <Plus size={16} className="mr-1.5" /> Save an idea
                            </Button>
                        }
                    />
                    {inspirations.length === 0 ? (
                        <div className="ns-card p-8 text-center text-[var(--ns-text2)]">
                            Nothing pinned yet. Save articles, places, and links to revisit later.
                        </div>
                    ) : (
                        <div className="columns-2 lg:columns-3 xl:columns-4 gap-5">
                            {inspirations.map((i) => (
                                <div key={i.inspiration_id} data-testid={HOME.inspirationCard} className="relative">
                                    <IdeaCard idea={i} onOpen={setDetailIdea} />
                                    <Button
                                        onClick={(e) => { e.stopPropagation(); setPinTarget(i); }}
                                        size="sm"
                                        data-testid={HOME.pinIdeaButton}
                                        className="absolute top-3 right-3 rounded-full h-8 px-3 text-xs text-white shadow"
                                        style={{ background: 'var(--ns-navy)' }}
                                    >
                                        <Pin size={12} className="mr-1" /> Pin
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Wishlist */}
                <section className="mb-16" data-testid={HOME.wishlistSection}>
                    <SectionHeader eyebrow="Dream list" title="Wishlist" />
                    {wishlist.length === 0 ? (
                        <div className="ns-card p-8 text-center text-[var(--ns-text2)]">
                            No wishes yet. Add destinations you want to visit someday.
                        </div>
                    ) : (
                        <div className="ns-card p-4 sm:p-6">
                            {wishlist.map((w) => (
                                <WishlistRow key={w.wishlist_id} item={w} onStart={setStartTarget} />
                            ))}
                        </div>
                    )}
                </section>
            </main>

            <NewTripDialog
                open={newTripOpen}
                onOpenChange={setNewTripOpen}
                onCreated={() => loadAll()}
            />
            <SaveIdeaDialog
                open={saveIdeaOpen}
                onOpenChange={setSaveIdeaOpen}
                onSaved={() => loadAll()}
            />
            <PinIdeaDialog
                open={!!pinTarget}
                onOpenChange={(o) => { if (!o) setPinTarget(null); }}
                inspiration={pinTarget}
                trips={trips}
                onPinned={() => { setPinTarget(null); loadAll(); }}
            />
            <StartPlanningDialog
                open={!!startTarget}
                onOpenChange={(o) => { if (!o) setStartTarget(null); }}
                item={startTarget}
                onStarted={() => { setStartTarget(null); loadAll(); }}
            />
            <IdeaDetailDialog
                open={!!detailIdea}
                onOpenChange={(o) => { if (!o) setDetailIdea(null); }}
                idea={detailIdea}
            />
            <OnboardingModal
                open={onboardingOpen}
                onClose={() => setOnboardingOpen(false)}
            />
            <Footer />
        </div>
    );
}
